import React, { useState, useRef, useEffect } from "react";
import { FiX, FiUpload } from "react-icons/fi";
import '../styles/addTeamModal.css';
import { BASE_URL } from '../constants/config';
import defaultTeamLogo from "../images/defaultTeamLogo.png";

export default function EditTeamModal({ isOpen, onClose, onSave, team }) {
    const [teamName, setTeamName] = useState('');
    const [shortName, setShortName] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [shouldDeleteLogo, setShouldDeleteLogo] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef(null);

    // Инициализация данных команды при открытии модального окна
    useEffect(() => {
        if (isOpen && team) {
            setTeamName(team.name || '');
            setShortName(team.short_name || '');
            
            // Устанавливаем превью текущего логотипа
            const currentLogo = team.image || team.logo_url || team.logo || team.team_logo || '';
            if (currentLogo && currentLogo !== '' && currentLogo !== '/images/others/defaultLogo.png') {
                const logoUrl = currentLogo.startsWith('http') ? currentLogo : BASE_URL.url + currentLogo;
                setLogoPreview(logoUrl);
            } else {
                setLogoPreview('');
            }
            
            setLogoFile(null);
            setShouldDeleteLogo(false);
            setErrors({});
        }
    }, [isOpen, team]);

    const handleClose = () => {
        if (!isSaving) {
            resetForm();
            onClose();
        }
    };

    const resetForm = () => {
        setTeamName('');
        setShortName('');
        setLogoFile(null);
        setLogoPreview('');
        setShouldDeleteLogo(false);
        setErrors({});
        setIsSaving(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Проверка типа файла
            if (!file.type.startsWith('image/')) {
                setErrors({ ...errors, logo: 'Пожалуйста, выберите изображение' });
                return;
            }

            // Проверка размера файла (макс 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setErrors({ ...errors, logo: 'Размер файла не должен превышать 5MB' });
                return;
            }

            setLogoFile(file);
            setShouldDeleteLogo(false); // Убираем флаг удаления
            setErrors({ ...errors, logo: null });

            // Создаем превью
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveLogo = () => {
        // Если есть новый загруженный файл - просто отменяем загрузку
        if (logoFile) {
            setLogoFile(null);
            // Возвращаем оригинальный логотип команды
            const currentLogo = team.image || team.logo_url || team.logo || team.team_logo || '';
            if (currentLogo && currentLogo !== '' && currentLogo !== '/images/others/defaultLogo.png') {
                const logoUrl = currentLogo.startsWith('http') ? currentLogo : BASE_URL.url + currentLogo;
                setLogoPreview(logoUrl);
            } else {
                setLogoPreview('');
            }
        } else {
            // Если файл не загружен, значит хотим удалить существующий логотип
            setShouldDeleteLogo(true);
            setLogoPreview('');
        }
        
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!teamName.trim()) {
            newErrors.teamName = 'Название команды обязательно';
        }

        if (shortName && shortName.length > 3) {
            newErrors.shortName = 'Короткое название не должно превышать 3 буквы';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);

        try {
            await onSave({
                name: teamName.trim(),
                shortName: shortName.trim() || null,
                logoFile: logoFile,
                shouldDeleteLogo: shouldDeleteLogo
            });
            resetForm();
        } catch (error) {
            console.error('Error updating team:', error);
            const errorMessage = error.message || 'Ошибка при обновлении команды';
            setErrors({ ...errors, submit: errorMessage });
        } finally {
            setIsSaving(false);
        }
    };

    const handleShortNameChange = (e) => {
        const value = e.target.value.toUpperCase();
        if (value.length <= 3) {
            setShortName(value);
            setErrors({ ...errors, shortName: null });
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !isSaving) {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape' && !isSaving) {
            handleClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="addTeamModalOverlay" onClick={handleClose}>
            <div className="addTeamModalContent" onClick={(e) => e.stopPropagation()}>
                <div className="addTeamModalHeader">
                    <h2 className="addTeamModalTitle">Редактировать команду</h2>
                    <button 
                        className="addTeamModalCloseBtn" 
                        onClick={handleClose}
                        disabled={isSaving}
                    >
                        <FiX />
                    </button>
                </div>

                <div className="addTeamModalBody">
                    {/* Название команды */}
                    <div className="addTeamFormGroup">
                        <label className="addTeamFormLabel">
                            Название команды <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            className={`addTeamFormInput ${errors.teamName ? 'error' : ''}`}
                            placeholder="Введите название команды"
                            value={teamName}
                            onChange={(e) => {
                                setTeamName(e.target.value);
                                setErrors({ ...errors, teamName: null });
                            }}
                            onKeyDown={handleKeyDown}
                            disabled={isSaving}
                            autoFocus
                        />
                        {errors.teamName && (
                            <span className="addTeamFormError">{errors.teamName}</span>
                        )}
                    </div>

                    {/* Короткое название */}
                    <div className="addTeamFormGroup">
                        <label className="addTeamFormLabel">
                            Короткое название (макс. 3 буквы)
                        </label>
                        <input
                            type="text"
                            className={`addTeamFormInput ${errors.shortName ? 'error' : ''}`}
                            placeholder="ABC"
                            value={shortName}
                            onChange={handleShortNameChange}
                            onKeyDown={handleKeyDown}
                            disabled={isSaving}
                            maxLength={3}
                        />
                        {errors.shortName && (
                            <span className="addTeamFormError">{errors.shortName}</span>
                        )}
                    </div>

                    {/* Логотип */}
                    <div className="addTeamFormGroup">
                        <label className="addTeamFormLabel">Логотип команды</label>
                        
                        {!logoPreview ? (
                            <div className="addTeamLogoUpload">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    disabled={isSaving}
                                    style={{ display: 'none' }}
                                    id="logoFileInput"
                                />
                                <label 
                                    htmlFor="logoFileInput" 
                                    className={`addTeamLogoUploadLabel ${isSaving ? 'disabled' : ''}`}
                                >
                                    <FiUpload className="uploadIcon" />
                                    <span>Выберите изображение</span>
                                    <small>PNG, JPG, GIF до 5MB</small>
                                </label>
                            </div>
                        ) : (
                            <div className="addTeamLogoPreview">
                                <img 
                                    src={logoPreview} 
                                    alt="Team logo preview"
                                    onError={(e) => { e.target.src = defaultTeamLogo; }}
                                />
                                <button 
                                    type="button"
                                    className="addTeamLogoRemoveBtn"
                                    onClick={handleRemoveLogo}
                                    disabled={isSaving}
                                    title={logoFile ? "Отменить загрузку" : "Удалить логотип"}
                                >
                                    <FiX />
                                </button>
                                {!logoFile && (
                                    <button
                                        type="button"
                                        className="addTeamLogoChangeBtn"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isSaving}
                                    >
                                        Изменить логотип
                                    </button>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    disabled={isSaving}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        )}
                        
                        {errors.logo && (
                            <span className="addTeamFormError">{errors.logo}</span>
                        )}
                        
                        <small className="addTeamFormHint">
                            Нажмите на красный крестик для установки логотипа по умолчанию
                        </small>
                    </div>

                    {errors.submit && (
                        <div className="addTeamFormError" style={{ marginTop: '10px' }}>
                            {errors.submit}
                        </div>
                    )}
                </div>

                <div className="addTeamModalFooter">
                    <button 
                        className="addTeamBtnCancel" 
                        onClick={handleClose}
                        disabled={isSaving}
                    >
                        Отмена
                    </button>
                    <button 
                        className="addTeamBtnSave" 
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
            </div>
        </div>
    );
}
